import { useState } from "react";
import { BedDouble, Bath, Square } from "lucide-react";
import { useAppContext } from "../../context/AppContext";
import { fmtPrice } from "../../assets/data";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=600&q=80";

function buildUnits(p) {
  const base = {
    type: p.type,
    rooms: p.rooms || 2,
    baths: p.baths || 1,
    area: p.area || 100,
    priceEGP: p.priceEGP || 0,
    priceSAR: p.priceSAR,
    priceAED: p.priceAED,
  };

  const scales =
    base.rooms <= 1
      ? [1, 1.35, 1.75]
      : base.rooms === 2
      ? [0.72, 1, 1.32]
      : [0.65, 0.82, 1];

  return scales.map((s, i) => {
    const rooms = Math.max(1, Math.round(base.rooms * s));
    return {
      id: `u${i}`,
      type: base.type,
      rooms,
      baths: Math.max(1, Math.round(base.baths * s)),
      area: Math.round(base.area * s),
      priceEGP: Math.round(base.priceEGP * s),
      priceSAR: base.priceSAR ? Math.round(base.priceSAR * s) : null,
      priceAED: base.priceAED ? Math.round(base.priceAED * s) : null,
      img: p.photos?.[i] || p.img || FALLBACK_IMG,
    };
  });
}

function tabLabel(rooms, lang) {
  if (lang === "ar") return rooms === 1 ? "١ غرفة" : `${rooms} غرف`;
  return rooms === 1 ? "1 Bedroom" : `${rooms} Bedrooms`;
}

export default function UnitTypes({ p }) {
  const { lang, cur } = useAppContext();
  const units = p.units && p.units.length > 0
    ? p.units.map((u, i) => ({
        id: u.id || `u${i}`,
        type: u.type || p.type,
        rooms: parseInt(u.rooms) || 0,
        baths: parseInt(u.baths) || 0,
        area: parseFloat(u.area) || 0,
        priceEGP: parseFloat(u.priceEGP) || 0,
        priceSAR: u.priceSAR ? parseFloat(u.priceSAR) : null,
        priceAED: u.priceAED ? parseFloat(u.priceAED) : null,
        img: u.img || p.photos?.[i] || p.img || FALLBACK_IMG,
      }))
    : buildUnits(p);
  const [activeTab, setActiveTab] = useState("all");

  const tabs = [
    { id: "all", label: lang === "en" ? "All" : "الكل" },
    ...units.map((u) => ({
      id: u.id,
      label: tabLabel(u.rooms, lang),
    })),
  ];

  const displayed = activeTab === "all" ? units : units.filter((u) => u.id === activeTab);

  return (
    <section id="pd-units" className="pd-section">
      <div className="fadeUp" style={{ marginBottom: "2.5rem" }}>
        <span className="pd-section-label">
          {lang === "en" ? "Unit Configurations" : "تكوينات الوحدات"}
        </span>
        <h2 className="pd-section-title">
          {lang === "en" ? "Available Units" : "الوحدات المتاحة"}
        </h2>
        <div className="pd-section-bar" />
      </div>

      {/* Tabs */}
      <div
        className="fadeUp d1"
        style={{
          display: "flex",
          gap: ".55rem",
          flexWrap: "wrap",
          marginBottom: "2.5rem",
        }}
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`pd-tab${activeTab === tab.id ? " pd-tab-active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Unit cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
          gap: "1.25rem",
        }}
      >
        {displayed.map((unit, i) => (
          <div key={unit.id} className={`pd-unit-card fadeUp d${i + 1}`}>
            {/* Image */}
            <div
              className="ci"
              style={{ paddingTop: "60%", position: "relative", overflow: "hidden" }}
            >
              <img
                src={unit.img}
                alt={`${unit.type} ${unit.rooms}BR`}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
              />
              <div
                style={{
                  position: "absolute",
                  top: 12,
                  left: 12,
                  background: "rgba(10,22,40,.72)",
                  backdropFilter: "blur(8px)",
                  border: "1px solid rgba(201,168,76,.25)",
                  borderRadius: 6,
                  padding: "3px 10px",
                  color: "var(--gold)",
                  fontSize: ".65rem",
                  fontFamily: "var(--sans)",
                  letterSpacing: ".1em",
                  textTransform: "uppercase",
                }}
              >
                {unit.type}
              </div>
            </div>

            {/* Card body */}
            <div style={{ padding: "1.25rem" }}>
              <div
                style={{
                  fontFamily: "var(--serif)",
                  fontSize: "1.15rem",
                  color: "var(--navy)",
                  fontWeight: 400,
                  marginBottom: "1rem",
                }}
              >
                {tabLabel(unit.rooms, lang)}
              </div>

              {/* Specs */}
              <div
                style={{
                  display: "flex",
                  gap: "1.25rem",
                  marginBottom: "1.1rem",
                }}
              >
                {[
                  { icon: <BedDouble size={14} />, val: unit.rooms },
                  { icon: <Bath size={14} />, val: unit.baths },
                  { icon: <Square size={14} />, val: `${unit.area}m²` },
                ].map((s, j) => (
                  <div
                    key={j}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      color: "var(--gray)",
                      fontSize: ".82rem",
                      fontFamily: "var(--sans)",
                    }}
                  >
                    <span style={{ color: "var(--gold)" }}>{s.icon}</span>
                    {s.val}
                  </div>
                ))}
              </div>

              <div className="gline" style={{ marginBottom: "1rem" }} />

              <div>
                <div
                  style={{
                    fontSize: ".6rem",
                    color: "var(--gray)",
                    textTransform: "uppercase",
                    letterSpacing: ".15em",
                    fontFamily: "var(--sans)",
                    marginBottom: ".3rem",
                  }}
                >
                  {lang === "en" ? "Starting From" : "يبدأ من"}
                </div>
                <div
                  style={{
                    fontFamily: "var(--serif)",
                    fontSize: "1.3rem",
                    color: "var(--navy)",
                    fontWeight: 600,
                  }}
                >
                  {fmtPrice(unit, cur)}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
